const SESSION_MONTH_KEY = 'savannah.active-session-month';
const SESSION_MONTH_CHANGED_EVENT = 'active-session-month-changed';

const toMonthKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const fromMonthKey = (monthKey: string): Date | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey.trim());
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }

  return new Date(year, monthIndex, 1);
};

const normalizeMonthStart = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

export const isSameMonth = (first: Date, second: Date): boolean => (
  first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth()
);

export const getActiveSessionMonth = (fallbackDate: Date): Date => {
  const fallbackMonth = normalizeMonthStart(fallbackDate);

  try {
    const savedMonthKey = sessionStorage.getItem(SESSION_MONTH_KEY);
    if (!savedMonthKey) {
      return fallbackMonth;
    }

    return fromMonthKey(savedMonthKey) || fallbackMonth;
  } catch {
    return fallbackMonth;
  }
};

export const setActiveSessionMonth = (date: Date): void => {
  const normalized = normalizeMonthStart(date);
  const monthKey = toMonthKey(normalized);

  try {
    const currentMonthKey = sessionStorage.getItem(SESSION_MONTH_KEY);
    if (currentMonthKey === monthKey) {
      return;
    }

    sessionStorage.setItem(SESSION_MONTH_KEY, monthKey);
  } catch {
    return;
  }

  window.dispatchEvent(new CustomEvent<string>(SESSION_MONTH_CHANGED_EVENT, { detail: monthKey }));
};

export const subscribeToActiveSessionMonth = (listener: (month: Date) => void): (() => void) => {
  const notifyFromMonthKey = (monthKey: string | null) => {
    if (!monthKey) {
      return;
    }

    const parsed = fromMonthKey(monthKey);
    if (parsed) {
      listener(parsed);
    }
  };

  const handleMonthChanged = (event: Event) => {
    const customEvent = event as CustomEvent<string>;
    notifyFromMonthKey(customEvent.detail || null);
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== SESSION_MONTH_KEY) {
      return;
    }

    notifyFromMonthKey(event.newValue);
  };

  window.addEventListener(SESSION_MONTH_CHANGED_EVENT, handleMonthChanged as EventListener);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(SESSION_MONTH_CHANGED_EVENT, handleMonthChanged as EventListener);
    window.removeEventListener('storage', handleStorage);
  };
};
